import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import axios from "axios";
import Toast from "./Toaster";
import { toast } from "react-hot-toast";
import ReactLoading from "react-loading";
import { logoutUser, setMember, setUser } from "../Slices/auth";
import mode from "./Mode";
import { useNavigate } from "react-router-dom";
import { memberRegisterSchema } from "../schemas";
import Overlay from "./overlay";
import { useLocation } from "react-router-dom";

import PhoneUpdate from "./phoneUpdate";
import { normalizePhones } from "./MultiPhoneInput";


const MyAccount = () => {
  const [openPhoneModal, setOpenPhoneModal] = useState(false);

  const [originalPhones, setOriginalPhones] = useState([]);

  //authorization
  const user = useSelector((state) => state.auth.user);
  const member = useSelector((state) => state.auth.member);
  const names = member.member.name.split(" ");
  const token = useSelector((state) => state.auth.token);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const baseurl = mode
    ? "https://backend.portal.jkusdachurch.org/general/"
    : "http://127.0.0.1:8000/general/";

  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  if (token) {
    config.headers["Authorization"] = `Token ${token}`;
  }
  const dispatch = useDispatch();

  useEffect(() => {
  if (location.state?.requirePhoneUpdate) {
    setShowInfoModal(true);

    // clear state so refresh does not reopen modal
    navigate(location.pathname, { replace: true });
  }
}, []);

  useEffect(() => {
    if (user == null) {
      toast.error("Unauthorized, login", {
        duration: 5000,
        position: "bottom-left",
      });
      navigate("/login", { state: { reason: "Unauthorized" } });
    }
  }, [user, navigate]);

  const phonesChanged = () => {
    const current = normalizePhones(values.phone_numbers).filter(
      (p) => p.trim() !== "",
    );

    const original = normalizePhones(originalPhones).filter(
      (p) => p.trim() !== "",
    );

    // 1️⃣ length change
    if (current.length !== original.length) return true;

    // 2️⃣ primary change
    if (current[0] !== original[0]) return true;

    // 3️⃣ compare rest ignoring order
    const restCurrent = [...current.slice(1)].sort();
    const restOriginal = [...original.slice(1)].sort();

    for (let i = 0; i < restCurrent.length; i++) {
      if (restCurrent[i] !== restOriginal[i]) {
        return true;
      }
    }

    return false;
  };

  const fetchPhonesAndOpen = async () => {
    try {
      const res = await axios.get(
        baseurl + "get-member-phones/" + user.username + "/",
        config,
      );

      const phones = res.data.phone_numbers?.length
        ? normalizePhones(res.data.phone_numbers)
        : [""];

      setOriginalPhones(phones);
      setFieldValue("phone_numbers", phones);
      setOpenPhoneModal(true);
    } catch (err) {
      toast.error("Failed to load phone numbers");
    }
  };

  const fetchPhones = async () => {
    try {
      const res = await axios.get(
        baseurl + "get-member-phones/" + user.username + "/",
        config,
      );

      setFieldValue(
        "phone_numbers",
        normalizePhones(
          res.data.phone_numbers?.length ? res.data.phone_numbers : [""],
        ),
      );
    } catch (err) {
      toast.error("Failed to load phone numbers");
    }
  };

  useEffect(() => {
    if (user?.username) {
      fetchPhones();
    }
  }, [user]);

  const savePhones = async () => {
    const phones = values.phone_numbers
      .map((p) => p.trim())
      .filter((p) => p !== "");

    if (!phones.length) {
      toast.error("Primary phone number is required");
      return;
    }

    try {
      await axios.put(
        baseurl + "update-member-phones/" + user.username + "/",
        {
          phone_numbers: phones,
          primary_phone: phones[0],
        },
        config,
      );

      toast.success("Phone numbers updated");
      setOriginalPhones(phones);
      setOpenPhoneModal(false);
    } catch (err) {
      toast.error("Failed to update phone numbers");
    }
  };

  const onSubmit = (values) => {
    setIsLoading(true);

    axios
      .put(
        baseurl + "update-member/" + user.username + "/",
        {
          user: {
            username: values.email,
            email: values.email,
          },
          member: {
            name: values.first_name + " " + values.last_name,
            course: values.course,
            baptismal_status: Number(values.baptised),
            yos: Number(values.intake),
            gender: values.gender,
            regno: values.reg_no,
          },
          authMember: {
            next_of_kin: values.next_of_kin,
            home_church: values.home_church,
          },
        },
        config,
      )
      .then((response) => {
        dispatch(setMember(response.data));
        dispatch(setUser(response.data.user));

        toast.success("Successfully updated your profile");

        // Reset form to updated values
        resetForm({ values });
      })

      .catch((err) => {
        toast.error("An error occurred");
        if (err.response?.status === 401) {
          dispatch(logoutUser());
          navigate("/login");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const {
    values,
    errors,
    handleBlur,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    dirty,
    isValid,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: names[0],
      last_name: names.length > 0 ? names[1] : "",
      course: member.member.course,
      next_of_kin: member.next_of_kin,
      home_church: member.home_church,
      baptised: member.member.baptismal_status,
      phone_numbers: member.phone?.member_phone
        ? [member.phone.member_phone]
        : [""],

      intake: member.member.yos,
      gender: member.member.gender,
      reg_no: member.member.regno,
      username: user ? user.username : "",
      email: user ? user.email : "",
    },
    validationSchema: memberRegisterSchema,
    onSubmit,
  });

  return (
    <>
      <Toast />
      <Overlay />
      <br />
      <br />
      <div className="container content-section">
        <div
          className="row d-flex flex-column align-items-center justify-content-center m-auto"
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <div
            className="content-section content col-md-10 m-auto"
            style={{ backgroundColor: "white" }}
          >
            <form onSubmit={handleSubmit} className="forms">
              <fieldset>
                <legend className="mb-4" style={{ color: "#228bdf" }}>
                  Update Details
                </legend>
                <div className="row">
                  <div className="form-group col-md-4">
                    <label htmlFor="inputPassword4" className="form-control-lg">
                      Email
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Email"
                      style={
                        errors.email && touched.email
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.email && touched.email && (
                      <p className="error">{errors.email}</p>
                    )}
                  </div>
                  <div className="form-group col-md-4">
                    <label htmlFor="inputEmail4" className="form-control-lg">
                      First Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="first_name"
                      value={values.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="First Name"
                      style={
                        errors.first_name && touched.first_name
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.first_name && touched.first_name && (
                      <p className="error">{errors.first_name}</p>
                    )}
                  </div>
                  <div className="form-group col-md-4">
                    <label htmlFor="inputPassword4" className="form-control-lg">
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="last_name"
                      value={values.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Last Name"
                      style={
                        errors.last_name && touched.last_name
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.last_name && touched.last_name && (
                      <p className="error">{errors.last_name}</p>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="form-group col-md-6">
                    <label className="form-control-lg"></label>

                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      onClick={() => fetchPhonesAndOpen()}
                    >
                      Manage phone numbers (
                      {values.phone_numbers.filter((p) => p.trim()).length})
                    </button>

                    {errors.phone_numbers && touched.phone_numbers && (
                      <p className="error">{errors.phone_numbers}</p>
                    )}
                  </div>

                  <div className="form-group col-md-3">
                    <label htmlFor="inputPassword4" className="form-control-lg">
                      Gender
                    </label>
                    <select
                      className="form-select"
                      name="gender"
                      value={values.gender}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Gender"
                      style={
                        errors.phone_number && touched.phone_number
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    >
                      <option value="M">MALE</option>
                      <option value="F">FEMALE</option>
                    </select>
                    {errors.phone_number && touched.phone_number && (
                      <p className="error">{errors.phone_number}</p>
                    )}
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="inputEmail4" className="form-control-lg">
                      Intake
                    </label>
                    <select
                      className="form-select"
                      name="intake"
                      value={values.intake}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Year of Intake"
                      style={
                        errors.intake && touched.intake
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    >
                      <option value={values.intake} selected>
                        {values.intake}
                      </option>
                      <option value="2022">2022</option>
                      <option value="2021">2021</option>
                      <option value="2020">2020</option>
                      <option value="2019">2019</option>
                      <option value="2018">2018</option>
                      <option value="2017">2017</option>
                    </select>
                    {errors.intake && touched.intake && (
                      <p className="error">{errors.intake}</p>
                    )}
                  </div>
                  <div className="form-group col-md-3">
                    <label className="form-control-lg">Baptised</label>
                    <select
                      className="form-select"
                      name="baptised"
                      value={values.baptised}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Baptised or not"
                      style={
                        errors.baptised && touched.baptised
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    >
                      <option value={values.baptised} selected>
                        {values.baptised ? "YES" : "NO"}
                      </option>
                      <option value="1" selected>
                        {values.baptised ? "NO" : "YES"}
                      </option>
                    </select>
                    {errors.baptised && touched.baptised && (
                      <p className="error">{errors.baptised}</p>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="form-group col-md-6">
                    <label className="form-control-lg">Course</label>
                    <input
                      type="text"
                      className="form-control"
                      name="course"
                      value={values.course}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Course"
                      style={
                        errors.course && touched.course
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.course && touched.course && (
                      <p className="error">{errors.course}</p>
                    )}
                  </div>
                  <div className="form-group col-md-6">
                    <label className="form-control-lg">
                      Registration Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="reg_no"
                      value={values.reg_no}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="School Reg Number"
                      style={
                        errors.course && touched.course
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.reg_no && touched.reg_no && (
                      <p className="error">{errors.reg_no}</p>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="form-group col-md-6">
                    <label className="form-control-lg">Next of Kin</label>
                    <input
                      type="text"
                      className="form-control"
                      name="next_of_kin"
                      value={values.next_of_kin}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Phone No: 07...or 01..."
                      style={
                        errors.next_of_kin && touched.next_of_kin
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.next_of_kin && touched.next_of_kin && (
                      <p className="error">{errors.next_of_kin}</p>
                    )}
                  </div>
                  <div className="form-group col-md-6">
                    <label className="form-control-lg">Home Church</label>
                    <input
                      type="text"
                      className="form-control"
                      name="home_church"
                      value={values.home_church}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Home Church"
                      style={
                        errors.home_church && touched.home_church
                          ? { borderColor: "#fc8181" }
                          : {}
                      }
                    />
                    {errors.home_church && touched.home_church && (
                      <p className="error">{errors.home_church}</p>
                    )}
                  </div>
                </div>
              </fieldset>
              <div className="form-group pt-3">
                <button
                  className="btn btn-outline-info"
                  type="submit"
                  disabled={isLoading || !dirty || !isValid}
                  style={{ borderRadius: "0.5rem" }}
                >
                  {" "}
                  {isLoading ? (
                    <ReactLoading
                      type="spin"
                      color="rgb(11, 43, 66)"
                      height={25}
                      width={25}
                    />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
        <br />
      </div>



      {showInfoModal && (
  <div
    className={`force-modal-overlay fade-in ${
      isClosing ? "fade-out" : ""
    }`}
  >
    <div
      className={`force-modal pop-in ${
        isClosing ? "pop-out" : ""
      }`}
    >
      <h5 style={{ marginBottom: "1rem" }}>
        Please check if your details are up-to-date.
      </h5>

      <p>Remember you can now add multiple phone numbers.</p>

      <button
        className="btn btn-primary mt-3 pulse-btn"
        onClick={() => {
          setIsClosing(true);

          setTimeout(() => {
            setShowInfoModal(false);
            setIsClosing(false);
          }, 700);
        }}
      >
        OK
      </button>

      <div className="glitter-container"></div>
    </div>
  </div>
)}

      <PhoneUpdate
        page="myaccount"
        open={openPhoneModal}
        onClose={() => setOpenPhoneModal(false)}
        values={values}
        setFieldValue={setFieldValue}
        onSave={savePhones}
        disableSave={!phonesChanged()}
      />
    </>
  );
};

export default MyAccount;
